import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getBookingLimitHistory } from 'api/marketing';
import { IBookingLimitChange } from 'types/bookingLimit';
import { formatHistoryAt } from './helpers';
import s from './s.module.less';

interface Props {
    open: boolean;
    isPractice: boolean;
    /** group practice 才显示「Changed by」列：solo 只有本人，列出来是废信息 */
    isGroupPractice: boolean;
    onClose: () => void;
}

const ROLE_LABEL: Record<string, string> = {
    GROUP_OWNER: 'Group Owner',
    ADMIN: 'Admin',
};

/** 原型 .role-tag：admin 用品牌色、owner 用灰，两者必须能一眼分开 */
const ROLE_CLASS: Record<string, string> = {
    GROUP_OWNER: s.roleTagOwner,
    ADMIN: s.roleTagAdmin,
};

/**
 * 修改历史。**整页一份，不是每行一份** —— owner 问「这里最近改了什么」时要的是一个列表，
 * 不是八个。（产品原型里明确去掉了逐行的 History 链接。）
 *
 * 时间按浏览器时区展示：这里回答的是「谁在什么时候改的」，是操作记录，
 * 与按 practice owner 时区计算的周期窗口无关。
 */
const HistoryModal: React.FC<Props> = ({ open, isPractice, isGroupPractice, onClose }) => {
    const [rows, setRows] = useState<IBookingLimitChange[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getBookingLimitHistory();
            if (!result.error) {
                setRows(result?.data?.data || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open, fetchData]);

    const columns: ColumnsType<IBookingLimitChange> = [
        {
            title: 'Date and time',
            dataIndex: 'changedAt',
            render: (ms: number) => formatHistoryAt(ms),
        },
        ...(isGroupPractice
            ? [
                  {
                      title: 'Changed by',
                      key: 'by',
                      render: (_: unknown, r: IBookingLimitChange) => (
                          <span className={s.changedBy}>
                              <span>{r.operatorName}</span>
                              {ROLE_LABEL[r.operatorRole] && (
                                  <span
                                      className={`${s.roleTag} ${
                                          ROLE_CLASS[r.operatorRole] ?? s.roleTagOwner
                                      }`}
                                  >
                                      {ROLE_LABEL[r.operatorRole]}
                                  </span>
                              )}
                          </span>
                      ),
                  } as ColumnsType<IBookingLimitChange>[number],
              ]
            : []),
        // 设计稿的取值是「Weekly limit」，不是「Weekly」
        { title: 'Limit', dataIndex: 'periodType', render: () => 'Weekly limit' },
        {
            title: 'Previous',
            dataIndex: 'previousValue',
            // 首次设置没有前值，产品要求展示为破折号而不是留白
            render: (v: string | null) => v || '–',
        },
        { title: 'New', dataIndex: 'newValue' },
    ];

    return (
        <Modal
            open={open}
            title="Change history"
            footer={null}
            onCancel={onClose}
            width={860}
            className={s.modalShell}
        >
            <div className={s.modalSub}>
                Every change to {isPractice ? 'the practice' : 'your'} marketplace new patient limit,
                newest first. Times are in your timezone.
            </div>
            <Table
                className={s.historyTable}
                rowKey={(r) => `${r.changedAt}-${r.operatorUserId}`}
                columns={columns}
                dataSource={rows}
                loading={loading}
                pagination={false}
                size="middle"
                scroll={{ y: 420 }}
                locale={{ emptyText: 'No changes yet.' }}
            />
        </Modal>
    );
};

export default HistoryModal;
